import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiShield, FiUser, FiX, FiCheck, FiSearch } = FiIcons;
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';

const RoleImpersonationModal = ({ isOpen, onClose }) => {
  const { startImpersonation, effectiveRole, stopImpersonation } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');

  const roles = [
    { id: 'superadmin', label: 'Super Admin', description: 'Full platform access and control', icon: FiShield, color: 'text-red-500' },
    { id: 'admin', label: 'Admin', description: 'Content and user management', icon: FiShield, color: 'text-orange-500' },
    { id: 'merchant', label: 'Merchant/Provider', description: 'Manage services and bookings', icon: FiUser, color: 'text-blue-500' },
    { id: 'creator', label: 'Standard Creator', description: 'Regular creator access', icon: FiUser, color: 'text-green-500' }
  ];

  const filteredRoles = roles.filter(role =>
    role.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-[#1a1a1a] w-full max-w-lg max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 shadow-3xl flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-red-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiShield} className="text-red-500 w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Impersonate Role</h2>
              <p className="text-xs text-gray-400">Test platform from different perspectives</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full transition-colors">
            <SafeIcon icon={FiX} className="text-gray-400 w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-white/5">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-black/20 border border-white/5 rounded-xl text-sm text-white focus:ring-1 focus:ring-red-500/50 outline-none"
            />
          </div>
        </div>

        {/* Roles List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
          {filteredRoles.map((role) => (
            <button
              key={role.id}
              onClick={() => {
                startImpersonation(role.id);
                onClose();
              }}
              className={`w-full p-4 rounded-2xl border transition-all text-left flex items-center justify-between group ${
                effectiveRole === role.id
                  ? 'bg-red-500/10 border-red-500/50'
                  : 'bg-white/5 border-white/5 hover:border-white/20'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 bg-black/40 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110`}>
                  <SafeIcon icon={role.icon} className={`w-6 h-6 ${role.color}`} />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">{role.label}</h3>
                  <p className="text-xs text-gray-500">{role.description}</p>
                </div>
              </div>
              {effectiveRole === role.id && (
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="text-black w-4 h-4" />
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 bg-black/40">
          <button
            onClick={() => {
              stopImpersonation();
              onClose();
            }}
            className="w-full py-3 bg-white/5 hover:bg-white/10 text-white text-sm font-bold rounded-xl transition-all"
          >
            Reset to Original Role
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default RoleImpersonationModal;